import React, { useState } from 'react';
import {
  FaCheckCircle,
  FaExternalLinkAlt,
  FaPrint,
  FaTruck,
} from 'react-icons/fa';
import {
  previewForfyShippingRates,
  type ForfyShippingRatesResponse,
} from '../../../lib/forfyCommerceShippingRatesClient';
import {
  purchaseForfyShippingLabel,
  type ForfyShippingLabelPurchaseResponse,
} from '../../../lib/forfyCommerceShippingLabelClient';

import './ForfyShippingRatesPreview.css';

type ForfyShippingRatesPreviewProps = {
  selectedOrder: any;
  businessName: string;
};

type ShippingForm = {
  addressFrom: {
    name: string;
    company: string;
    street1: string;
    city: string;
    state: string;
    zip: string;
    country: string;
  };
  addressTo: {
    name: string;
    company: string;
    street1: string;
    city: string;
    state: string;
    zip: string;
    country: string;
  };
  parcel: {
    length: string;
    width: string;
    height: string;
    distance_unit: 'cm';
    weight: string;
    mass_unit: 'kg';
  };
};

const buildInitialForm = (businessName: string): ShippingForm => ({
  addressFrom: {
    name: businessName || 'Commerce Forfeo',
    company: businessName || '',
    street1: '',
    city: 'Québec',
    state: 'QC',
    zip: '',
    country: 'CA',
  },
  addressTo: {
    name: 'Client',
    company: '',
    street1: '',
    city: '',
    state: 'QC',
    zip: '',
    country: 'CA',
  },
  parcel: {
    length: '30',
    width: '20',
    height: '10',
    distance_unit: 'cm',
    weight: '1',
    mass_unit: 'kg',
  },
});

const ForfyShippingRatesPreview: React.FC<ForfyShippingRatesPreviewProps> = ({
  selectedOrder,
  businessName,
}) => {
  const [form, setForm] = useState<ShippingForm>(() => buildInitialForm(businessName));
  const [status, setStatus] = useState<'idle' | 'loading' | 'ready' | 'blocked'>('idle');
  const [result, setResult] = useState<ForfyShippingRatesResponse | null>(null);
  const [selectedRateId, setSelectedRateId] = useState('');
  const [showPurchaseConfirmation, setShowPurchaseConfirmation] = useState(false);

  // COMMERCE_UX1J_EXACT_PRODUCTION_UI_LOCK
  const [purchaseConfirmed, setPurchaseConfirmed] = useState(false);
  const [purchaseStatus, setPurchaseStatus] = useState<'idle' | 'loading' | 'success' | 'blocked'>('idle');
  const [purchaseResult, setPurchaseResult] = useState<ForfyShippingLabelPurchaseResponse | null>(null);

  // COMMERCE_UX1I_RATE_SELECTION_CONFIRMATION_LOCK
  const selectedRate =
    result?.rates.find((rate) => rate.id === selectedRateId) || null;

  if (!selectedOrder || selectedOrder.type !== 'order') {
    return null;
  }

  const updateAddress = (
    side: 'addressFrom' | 'addressTo',
    key: keyof ShippingForm['addressFrom'],
    value: string,
  ) => {
    setForm((current) => ({
      ...current,
      [side]: {
        ...current[side],
        [key]: value,
      },
    }));

    setResult(null);
    setStatus('idle');
    setSelectedRateId('');
    setShowPurchaseConfirmation(false);
    setPurchaseConfirmed(false);
    setPurchaseStatus('idle');
    setPurchaseResult(null);
  };

  const updateParcel = (
    key: keyof ShippingForm['parcel'],
    value: string,
  ) => {
    setForm((current) => ({
      ...current,
      parcel: {
        ...current.parcel,
        [key]: value,
      },
    }));

    setResult(null);
    setStatus('idle');
    setSelectedRateId('');
    setShowPurchaseConfirmation(false);
    setPurchaseConfirmed(false);
    setPurchaseStatus('idle');
    setPurchaseResult(null);
  };

  const handlePreview = async () => {
    setStatus('loading');
    setResult(null);
    setSelectedRateId('');
    setShowPurchaseConfirmation(false);
    setPurchaseConfirmed(false);
    setPurchaseStatus('idle');
    setPurchaseResult(null);

    const response = await previewForfyShippingRates({
      order: selectedOrder as Record<string, unknown>,
      addressFrom: form.addressFrom,
      addressTo: form.addressTo,
      parcel: form.parcel,
    });

    setResult(response);
    setStatus(response.ok ? 'ready' : 'blocked');
  };

  const handleProductionPurchase = async () => {
    if (
      !selectedRate ||
      !purchaseConfirmed ||
      purchaseStatus === 'loading' ||
      purchaseStatus === 'success'
    ) {
      return;
    }

    const orderReference =
      String(
        selectedOrder.label ||
        selectedOrder.id ||
        'Commande Forfeo',
      );

    const accepted =
      window.confirm(
        [
          'ACHAT RÉEL D’ÉTIQUETTE',
          '',
          `Commande : ${orderReference}`,
          `Transporteur : ${selectedRate.provider}`,
          `Service : ${selectedRate.service}`,
          `Coût : ${selectedRate.amount} ${selectedRate.currency}`,
          '',
          'Cette action achètera réellement cette étiquette avec le compte Shippo production.',
          '',
          'Confirmer cet achat réel?',
        ].join('\n'),
      );

    if (!accepted) {
      return;
    }

    setPurchaseStatus('loading');
    setPurchaseResult(null);

    const response =
      await purchaseForfyShippingLabel({
        rateId:
          selectedRate.id,

        orderReference,

        expectedAmount:
          selectedRate.amount,

        expectedCurrency:
          selectedRate.currency,
      });

    setPurchaseResult(
      response,
    );

    setPurchaseStatus(
      response.ok
        ? 'success'
        : 'blocked',
    );
  };

  return (
    <section
      className="forfy-shipping-rates-preview"
      data-forfy-secure-shipping-rates="ux1h"
    >
      <header>
        <div>
          <p><FaTruck /> Expédition sécurisée</p>
          <h5>Prévisualiser les tarifs</h5>
          <span>
            Commande : {selectedOrder.label || selectedOrder.id}
          </span>
        </div>
      </header>

      <div className="forfy-shipping-rates-preview__grid">
        <label>
          Adresse du commerce
          <input
            value={form.addressFrom.street1}
            onChange={(event) =>
              updateAddress('addressFrom', 'street1', event.currentTarget.value)
            }
            placeholder="123 rue Exemple"
          />
        </label>

        <label>
          Code postal commerce
          <input
            value={form.addressFrom.zip}
            onChange={(event) =>
              updateAddress('addressFrom', 'zip', event.currentTarget.value)
            }
            placeholder="G1A 0A1"
          />
        </label>

        <label>
          Nom du client
          <input
            value={form.addressTo.name}
            onChange={(event) =>
              updateAddress('addressTo', 'name', event.currentTarget.value)
            }
          />
        </label>

        <label>
          Adresse du client
          <input
            value={form.addressTo.street1}
            onChange={(event) =>
              updateAddress('addressTo', 'street1', event.currentTarget.value)
            }
            placeholder="456 rue Client"
          />
        </label>

        <label>
          Ville du client
          <input
            value={form.addressTo.city}
            onChange={(event) =>
              updateAddress('addressTo', 'city', event.currentTarget.value)
            }
            placeholder="Montréal"
          />
        </label>

        <label>
          Code postal client
          <input
            value={form.addressTo.zip}
            onChange={(event) =>
              updateAddress('addressTo', 'zip', event.currentTarget.value)
            }
            placeholder="H2X 1A1"
          />
        </label>
      </div>

      <div className="forfy-shipping-rates-preview__parcel">
        <label>
          Longueur cm
          <input
            value={form.parcel.length}
            onChange={(event) =>
              updateParcel('length', event.currentTarget.value)
            }
          />
        </label>

        <label>
          Largeur cm
          <input
            value={form.parcel.width}
            onChange={(event) =>
              updateParcel('width', event.currentTarget.value)
            }
          />
        </label>

        <label>
          Hauteur cm
          <input
            value={form.parcel.height}
            onChange={(event) =>
              updateParcel('height', event.currentTarget.value)
            }
          />
        </label>

        <label>
          Poids kg
          <input
            value={form.parcel.weight}
            onChange={(event) =>
              updateParcel('weight', event.currentTarget.value)
            }
          />
        </label>
      </div>

      <button
        type="button"
        className="forfy-shipping-rates-preview__button"
        onClick={handlePreview}
        disabled={status === 'loading'}
      >
        <FaTruck />
        {status === 'loading'
          ? 'Recherche des tarifs...'
          : 'Prévisualiser les tarifs sécurisés'}
      </button>

      {result && (
        <div
          className={`forfy-shipping-rates-preview__result ${
            result.ok ? 'is-ready' : 'is-blocked'
          }`}
        >
          <strong>
            {result.ok
              ? 'Tarifs disponibles'
              : 'Tarifs à compléter'}
          </strong>

          {!result.ok && (
            <span>
              {result.message || 'Tarifs indisponibles.'}
              {result.missingFields?.length
                ? ` Champs manquants : ${result.missingFields.join(', ')}.`
                : ''}
            </span>
          )}

          {result.ok && result.rates.length === 0 && (
            <span>
              Aucun tarif retourné pour ces informations.
            </span>
          )}

          {result.ok &&
            result.rates.slice(0, 6).map((rate) => (
              <button
                key={`${rate.id}-${rate.provider}-${rate.service}`}
                type="button"
                className={`forfy-shipping-rates-preview__rate ${
                  selectedRateId === rate.id ? 'is-selected' : ''
                }`}
                onClick={() => {
                  setSelectedRateId(rate.id);
                  setShowPurchaseConfirmation(false);
    setPurchaseConfirmed(false);
    setPurchaseStatus('idle');
    setPurchaseResult(null);
                }}
                data-shipping-rate-id={rate.id}
              >
                <span>{rate.provider}</span>
                <strong>{rate.service}</strong>
                <small>
                  {rate.amount} {rate.currency}
                  {rate.estimatedDays !== null
                    ? ` · ${rate.estimatedDays} jour(s)`
                    : ''}
                </small>

                <b>
                  {selectedRateId === rate.id
                    ? 'Sélectionné'
                    : 'Choisir ce tarif'}
                </b>
              </button>
            ))}

          {selectedRate && (
            <section
              className="forfy-shipping-rates-preview__selected"
              data-forfy-selected-shipping-rate="ux1i"
            >
              <div>
                <p>Tarif sélectionné</p>
                <h6>
                  {selectedRate.provider} — {selectedRate.service}
                </h6>
                <strong>
                  {selectedRate.amount} {selectedRate.currency}
                </strong>
                <span>
                  {selectedRate.estimatedDays !== null
                    ? `Livraison estimée : ${selectedRate.estimatedDays} jour(s)`
                    : 'Délai selon transporteur'}
                </span>
              </div>

              <button
                type="button"
                onClick={() => {
                  setShowPurchaseConfirmation(true);
                  setPurchaseConfirmed(false);
                  setPurchaseStatus('idle');
                  setPurchaseResult(null);
                }}
              >
                Continuer vers la confirmation
              </button>
            </section>
          )}

          {selectedRate && showPurchaseConfirmation && (
            <section
              className="forfy-shipping-rates-preview__confirmation"
              data-forfy-label-purchase-confirmation="ux1i"
            >
              <p>Confirmation avant achat</p>

              <h6>
                {selectedRate.provider} — {selectedRate.service}
              </h6>

              <dl>
                <div>
                  <dt>Commande</dt>
                  <dd>{selectedOrder.label || selectedOrder.id}</dd>
                </div>

                <div>
                  <dt>Coût de l’étiquette</dt>
                  <dd>
                    {selectedRate.amount} {selectedRate.currency}
                  </dd>
                </div>

                <div>
                  <dt>Format prévu</dt>
                  <dd>PDF 4 × 6</dd>
                </div>
              </dl>

              <div className="forfy-shipping-rates-preview__warning">
                <strong>Attention</strong>
                <span>
                  L’achat réel est maintenant activé en production.
                  Deux confirmations humaines sont obligatoires.
                </span>
              </div>

              {/* UX1I legacy source contract: Achat réel non activé */}

              <label className="forfy-shipping-rates-preview__purchase-check">
                <input
                  type="checkbox"
                  checked={purchaseConfirmed}
                  disabled={purchaseStatus === 'success'}
                  onChange={(event) =>
                    setPurchaseConfirmed(
                      event.currentTarget.checked,
                    )
                  }
                />

                <span>
                  Je confirme l’achat réel de cette étiquette pour{' '}
                  <strong>
                    {selectedRate.amount} {selectedRate.currency}
                  </strong>.
                </span>
              </label>

              <button
                type="button"
                className="forfy-shipping-rates-preview__purchase-button"
                disabled={
                  !purchaseConfirmed ||
                  purchaseStatus === 'loading' ||
                  purchaseStatus === 'success'
                }
                data-real-label-purchase-enabled="true"
                onClick={handleProductionPurchase}
              >
                {purchaseStatus === 'loading'
                  ? 'Achat de l’étiquette...'
                  : purchaseStatus === 'success'
                    ? 'Étiquette achetée'
                    : 'Confirmer et acheter l’étiquette'}
              </button>

              {purchaseResult && purchaseStatus === 'blocked' && (
                <div className="forfy-shipping-rates-preview__purchase-error">
                  <strong>Achat non complété</strong>

                  <span>
                    {purchaseResult.message ||
                      'Shippo n’a pas confirmé l’achat.'}
                  </span>

                  {purchaseResult.error === 'rate_amount_changed' &&
                    purchaseResult.currentAmount && (
                      <small>
                        Nouveau tarif : {purchaseResult.currentAmount}{' '}
                        {purchaseResult.currentCurrency ||
                          selectedRate.currency}.
                        Recalcule les tarifs avant de continuer.
                      </small>
                    )}

                  {purchaseResult.safeRetry === false && (
                    <small>
                      Ne relance pas automatiquement l’achat.
                      Vérifie d’abord les transactions Shippo.
                    </small>
                  )}
                </div>
              )}
            </section>
          )}

          {purchaseResult && purchaseStatus === 'success' && (
            <section
              className="forfy-shipping-rates-preview__purchase-success"
              data-forfy-production-label-success="ux1j"
            >
              <div className="forfy-shipping-rates-preview__success-head">
                <FaCheckCircle />

                <div>
                  <p>Étiquette achetée</p>

                  <h6>
                    {purchaseResult.provider || selectedRate?.provider}
                    {' — '}
                    {purchaseResult.service || selectedRate?.service}
                  </h6>
                </div>
              </div>

              {purchaseResult.duplicatePrevented && (
                <div className="forfy-shipping-rates-preview__duplicate">
                  Une transaction SUCCESS existait déjà pour ce tarif.
                  Forfeo a récupéré cette étiquette sans effectuer
                  un deuxième achat.
                </div>
              )}

              <dl>
                <div>
                  <dt>Transaction</dt>
                  <dd>{purchaseResult.transactionId || '—'}</dd>
                </div>

                <div>
                  <dt>Numéro de suivi</dt>
                  <dd>{purchaseResult.trackingNumber || 'Non fourni'}</dd>
                </div>

                <div>
                  <dt>Statut</dt>
                  <dd>{purchaseResult.status || 'SUCCESS'}</dd>
                </div>

                <div>
                  <dt>Format</dt>
                  <dd>{purchaseResult.labelFileType || 'PDF_4x6'}</dd>
                </div>
              </dl>

              <div className="forfy-shipping-rates-preview__purchase-actions">
                {purchaseResult.labelUrl && (
                  <a
                    href={purchaseResult.labelUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <FaPrint />
                    Ouvrir / imprimer l’étiquette
                  </a>
                )}

                {purchaseResult.trackingUrl && (
                  <a
                    href={purchaseResult.trackingUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <FaExternalLinkAlt />
                    Suivre le colis
                  </a>
                )}
              </div>
            </section>
          )}

          <small>
            La recherche de tarifs n’achète rien.
            L’achat réel exige le tarif sélectionné,
            la case de confirmation et la seconde confirmation.
          </small>
        </div>
      )}
    </section>
  );
};

export default ForfyShippingRatesPreview;
